import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import Breadcrumbs from '@/components/Breadcrumbs';
import { Skeleton } from '@/components/Skeleton';
import { getAuthHeaders } from '@/data/getAuthHeaders';
import { useOrganizationId } from '@/hooks/useSwr';
import { getAgentBuilderToolServerPath } from '@/utils/constants';

interface Tool {
  name: string;
  description?: string;
  inputSchema?: {
    type: string;
    properties: Record<
      string,
      {
        type: string;
        description?: string;
        required?: boolean;
      }
    >;
    required?: string[];
  };
}

interface ToolsResponse {
  tools: Tool[];
}

interface ToolExecutionResponse {
  content?: Array<{
    type: string;
    text?: string;
  }>;
  isError?: boolean;
  auth_required?: boolean;
  auth_url?: string;
  auth_id?: string;
}

export function ToolPlayground() {
  const { toolName } = useParams<{ toolName: string }>();
  const organizationId = useOrganizationId();
  const [tool, setTool] = useState<Tool | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState<ToolExecutionResponse | null>(null);

  useEffect(() => {
    const fetchTool = async () => {
      try {
        setLoading(true);
        setError(null);

        const authHeaders = await getAuthHeaders();
        const headers = {
          'Content-Type': 'application/json',
          ...authHeaders,
        };

        if (organizationId) {
          headers['X-Tenant-Id'] = organizationId;
        }

        const response = await fetch(getAgentBuilderToolServerPath('mcp'), {
          method: 'POST',
          headers,
          body: JSON.stringify({
            method: 'tools/list',
            params: {},
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const responseData = await response.json();
        const data: ToolsResponse = responseData.result;
        const foundTool = data.tools?.find((t) => t.name === toolName);
        if (!foundTool) {
          throw new Error(`Tool "${toolName}" not found`);
        }
        setTool(foundTool);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch tool');
      } finally {
        setLoading(false);
      }
    };

    if (toolName) {
      fetchTool();
    }
  }, [toolName, organizationId]);

  const handleInputChange = (key: string, value: any) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const executeTool = async () => {
    if (!tool) return;

    try {
      setExecuting(true);
      setResult(null);

      const authHeaders = await getAuthHeaders();
      const headers = {
        'Content-Type': 'application/json',
        ...authHeaders,
      };

      if (organizationId) {
        headers['X-Tenant-Id'] = organizationId;
      }

      const response = await fetch(getAgentBuilderToolServerPath('mcp'), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          method: 'tools/call',
          params: {
            name: tool.name,
            arguments: formData,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Check if the response indicates authentication is required
      let parsedResult = data.result;

      // Try to parse the content if it's a string that might contain auth info
      if (parsedResult?.content?.[0]?.text) {
        try {
          const textContent = parsedResult.content[0].text;
          const parsedContent = JSON.parse(textContent);

          if (parsedContent.auth_required) {
            parsedResult = {
              ...parsedResult,
              auth_required: parsedContent.auth_required,
              auth_url: parsedContent.auth_url,
              auth_id: parsedContent.auth_id,
            };
          }
        } catch (e) {
          // If parsing fails, just use the original result
        }
      }

      setResult(parsedResult);
    } catch (err) {
      setResult({
        content: [
          {
            type: 'text',
            text: `Error: ${
              err instanceof Error ? err.message : 'Unknown error'
            }`,
          },
        ],
        isError: true,
      });
    } finally {
      setExecuting(false);
    }
  };

  const renderFormField = (key: string, schema: any) => {
    const isRequired = tool?.inputSchema?.required?.includes(key);

    return (
      <div key={key} className="space-y-2">
        <label className="block text-sm font-medium">
          {key}
          {isRequired && <span className="ml-1 text-red-500">*</span>}
          <span className="ml-1 text-quaternary">Required</span>
        </label>
        {schema.description && (
          <p className="text-xs text-quaternary">{schema.description}</p>
        )}
        {schema.type === 'string' ? (
          <input
            type="text"
            placeholder={`Enter ${key}`}
            value={formData[key] || ''}
            onChange={(e) => handleInputChange(key, e.target.value)}
            className="focus:ring-primary/20 w-full rounded-md border border-secondary bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2"
          />
        ) : schema.type === 'number' || schema.type === 'integer' ? (
          <input
            type="number"
            placeholder={`Enter ${key}`}
            value={formData[key] || ''}
            onChange={(e) =>
              handleInputChange(
                key,
                e.target.value ? Number(e.target.value) : ''
              )
            }
            className="focus:ring-primary/20 w-full rounded-md border border-secondary bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2"
          />
        ) : schema.type === 'boolean' ? (
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData[key] || false}
              onChange={(e) => handleInputChange(key, e.target.checked)}
              className="rounded border-secondary"
            />
            <span className="text-sm">Enable {key}</span>
          </div>
        ) : (
          <textarea
            placeholder={`Enter ${key}`}
            value={formData[key] || ''}
            onChange={(e) => handleInputChange(key, e.target.value)}
            rows={3}
            className="focus:ring-primary/20 w-full rounded-md border border-secondary bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2"
          />
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div>
        <div className="border-b border-secondary px-4 py-3">
          <Breadcrumbs />
        </div>
        <div className="flex flex-col gap-6 px-9 py-11">
          <div className="flex items-center gap-4">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-8 w-24" />
          </div>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="space-y-4">
              <Skeleton className="h-6 w-24" />
              <Skeleton className="h-32 w-full" />
            </div>
            <div className="space-y-4">
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-32 w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !tool) {
    return (
      <div>
        <div className="border-b border-secondary px-4 py-3">
          <Breadcrumbs />
        </div>
        <div className="flex flex-col gap-6 px-9 py-11">
          <div className="rounded-md bg-red-50 p-4 text-red-800 dark:bg-red-900/20 dark:text-red-200">
            Error: {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="border-b border-secondary px-4 py-3">
        <Breadcrumbs />
      </div>
      <div className="flex flex-col gap-6 px-9 py-11">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-semibold">Tools Playground</h1>
          </div>
          <button
            type="button"
            onClick={executeTool}
            disabled={executing}
            className="flex items-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {executing ? 'Executing...' : 'Execute'}
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <h2 className="mb-2 text-lg font-medium">{tool.name}</h2>
            <p className="text-quaternary">
              {tool.description || 'Run a linkedin search'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Input</h3>
            <div className="space-y-4">
              {tool.inputSchema?.properties ? (
                Object.entries(tool.inputSchema.properties).map(
                  ([key, schema]) => renderFormField(key, schema)
                )
              ) : (
                <p className="text-quaternary">No input parameters required</p>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-medium">Response</h3>
            <div className="min-h-[200px] rounded-md border border-secondary bg-background p-4">
              {result ? (
                result.auth_required && result.auth_url ? (
                  // Show authentication required UI
                  <div className="flex flex-col items-center justify-center space-y-4 py-8">
                    <div className="rounded-full bg-blue-100 p-3">
                      <svg
                        className="h-6 w-6 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        />
                      </svg>
                    </div>
                    <div className="space-y-2 text-center">
                      <h4 className="font-medium text-gray-900">
                        Authentication Required
                      </h4>
                      <p className="max-w-md text-sm text-gray-600">
                        This tool requires authentication to access external
                        services. Click the button below to authenticate.
                      </p>
                    </div>
                    <a
                      href={result.auth_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                    >
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                      Authenticate with Service
                    </a>
                  </div>
                ) : (
                  // Show regular response
                  <pre className="overflow-auto whitespace-pre-wrap text-sm">
                    {result.content?.map((item, i) => (
                      <div
                        key={i}
                        className={result.isError ? 'text-red-500' : ''}
                      >
                        {item.text || JSON.stringify(item, null, 2)}
                      </div>
                    ))}
                  </pre>
                )
              ) : (
                <p className="text-quaternary">
                  No response yet. Run the tool to see results.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
